/**
 * <gg-header> — the roll-down header (sections/gg-header.liquid).
 *
 * All state lives on two attributes; CSS derives everything else:
 *   data-open  = '' | 'menu' | 'search' | 'cart'   → sheet rolled down + scrim
 *   data-panel = the panel shown in the sheet       → only changes on open/switch, so
 *                                                     the panel stays put while the
 *                                                     sheet rolls up
 *
 * Opening:
 *   · hover (fine pointers only) — intent delay in, grace period out, so the pointer
 *     can travel from a trigger into the sheet. A hover-opened panel closes when the
 *     pointer leaves the header (bar + sheet), UNLESS it is pinned.
 *   · click / tap / Enter — toggles and PINS (stays open until closed on purpose).
 *   · typing or focus inside a panel also pins it (never lose a half-typed search).
 * Closing: Escape (focus returns to the trigger), a scrim click, the open trigger,
 * the "Schließen (×)" line under the panels (data-close, like the scrim).
 *
 * The cart engine re-renders this section on every cart change; the morph contract
 * (see the section) keeps this element's attributes and the bar/menu/search subtrees
 * untouched, so nothing here needs re-binding after a morph — every listener is
 * delegated from this element.
 */

const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
const PHONE = window.matchMedia('(max-width: 749px)');
const OPEN_DELAY = 90;
const CLOSE_DELAY = 250;
const SEARCH_DEBOUNCE = 200;

class GgHeader extends HTMLElement {
  #pinned = false;
  #openTimer = 0;
  #closeTimer = 0;
  #searchTimer = 0;
  #searchAbort = null;
  #searchDefault = '';
  #cartAbort = null;

  connectedCallback() {
    const results = this.querySelector('[data-search-results]');
    this.#searchDefault = results ? results.innerHTML : '';

    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('keydown', this.#onDocumentKeydown);
    document.addEventListener('cart:update', this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#cartAbort?.abort();
    this.#unlockScroll();
  }

  get openPanel() {
    return this.dataset.open || '';
  }

  /* ── Open / close ───────────────────────────────────────────────────────── */

  open(panel, { pinned = false, focus = false } = {}) {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#pinned = this.#pinned || pinned;
    this.dataset.panel = panel;
    this.dataset.open = panel;

    for (const trigger of this.querySelectorAll('[data-trigger]')) {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.trigger === panel));
    }

    if (PHONE.matches) this.#lockScroll();
    if (focus) this.#focusPanel(panel);
  }

  close({ returnFocus = false } = {}) {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    const was = this.openPanel;
    if (!was) return;

    this.#pinned = false;
    this.dataset.open = '';
    for (const trigger of this.querySelectorAll('[data-trigger]')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    this.#unlockScroll();
    this.#resetTeaser();

    // Don't leave focus inside the hidden sheet (e.g. the auto-focused search field).
    const active = document.activeElement;
    if (!returnFocus && active instanceof HTMLElement && this.querySelector('.gg-hd__sheet')?.contains(active)) {
      active.blur();
    }

    if (returnFocus) this.querySelector(`[data-trigger="${was}"]`)?.focus();
  }

  #focusPanel(panel) {
    const target =
      panel === 'search'
        ? this.querySelector('[data-search-input]')
        : this.querySelector(`[data-panel-id="${panel}"] :is(a, button, input, textarea, select)`);
    // Wait one frame: the panel becomes display:block from the attribute change.
    requestAnimationFrame(() => target?.focus({ preventScroll: true }));
  }

  /**
   * A hover-opened panel stays open while the user is working inside it. The search
   * field is focused the moment Search opens, so an EMPTY search field is not work —
   * otherwise hovering Search would pin the panel open forever. Typing pins it.
   */
  #isBusy() {
    if (this.#pinned) return true;
    const sheet = this.querySelector('.gg-hd__sheet');
    const active = document.activeElement;
    if (!sheet || !active || !sheet.contains(active)) return false;
    return !(active.matches('[data-search-input]') && !active.value.trim());
  }

  #scheduleClose() {
    if (this.#isBusy()) return;
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => {
      if (!this.#isBusy()) this.close();
    }, CLOSE_DELAY);
  }

  /* ── Pointer ────────────────────────────────────────────────────────────── */

  #onPointerOver = (event) => {
    if (!HOVER.matches || event.pointerType === 'touch') return;
    const target = /** @type {Element} */ (event.target);

    // Category teaser: pointing at a collection link previews it in the stage.
    const teaserLink = target.closest('[data-teaser-key]');
    if (teaserLink) this.#showTeaser(teaserLink.getAttribute('data-teaser-key'));
    else if (!target.closest('[data-teaser-rail]')) this.#resetTeaser();

    // The scrim is "outside" — the page behind the header.
    if (target.closest('[data-close]')) {
      if (this.openPanel) this.#scheduleClose();
      return;
    }

    const trigger = target.closest('[data-trigger]');
    if (trigger) {
      const panel = trigger.dataset.trigger;
      clearTimeout(this.#closeTimer);
      clearTimeout(this.#openTimer);
      if (this.openPanel === panel) return;
      // Already open → switch instantly; closed → wait for intent.
      // Search puts the cursor straight into its field — hover and type.
      const focus = panel === 'search';
      if (this.openPanel && !this.#pinned) this.open(panel, { focus });
      else if (!this.openPanel) this.#openTimer = window.setTimeout(() => this.open(panel, { focus }), OPEN_DELAY);
      return;
    }

    // Any other bar link (Shop, Account, the crown) is a way out of the panel.
    if (target.closest('.gg-hd__bar a')) {
      clearTimeout(this.#openTimer);
      if (this.openPanel) this.#scheduleClose();
      return;
    }

    // Inside the bar or the sheet → stay.
    if (target.closest('.gg-hd__bar, .gg-hd__sheet')) {
      clearTimeout(this.#closeTimer);
    }
  };

  /** Leaving the window altogether (the scrim never sees it) also counts as out. */
  #onPointerLeave = (event) => {
    if (!HOVER.matches || event.pointerType === 'touch') return;
    clearTimeout(this.#openTimer);
    if (this.openPanel) this.#scheduleClose();
  };

  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // Scrim or the close line. A keyboard press (only the close line can get
    // one) hands focus back to the trigger instead of dropping it.
    if (target.closest('[data-close]')) {
      this.close({ returnFocus: event.detail === 0 });
      return;
    }

    const trigger = target.closest('[data-trigger]');
    if (!trigger) return;

    const panel = trigger.dataset.trigger;
    const viaKeyboard = event.detail === 0;

    if (this.openPanel === panel && (this.#pinned || !HOVER.matches)) {
      this.close({ returnFocus: viaKeyboard });
      return;
    }

    // A click on a hover-opened panel pins it instead of closing it.
    this.open(panel, { pinned: true, focus: viaKeyboard || panel === 'search' });
  };

  /* ── Keyboard / focus ───────────────────────────────────────────────────── */

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.openPanel) {
      event.stopPropagation();
      this.close({ returnFocus: true });
    }
  };

  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && this.openPanel) this.close();
  };

  /** Focus landing in a panel (tabbing, clicking a field) pins it. */
  #onFocusIn = (event) => {
    const target = /** @type {Element} */ (event.target);
    // (The search field is exempt: it is focused automatically on open — typing pins it.)
    if (this.openPanel && target.closest('.gg-hd__sheet') && !target.matches('[data-search-input]')) {
      this.#pinned = true;
    }
    const teaserLink = target.closest('[data-teaser-key]');
    if (teaserLink) this.#showTeaser(teaserLink.getAttribute('data-teaser-key'));
  };

  /* ── Category teaser ────────────────────────────────────────────────────── */

  #showTeaser(key) {
    const stage = this.querySelector('[data-panel-id="menu"] .gg-hd__stage-slot');
    if (!stage || !stage.querySelector(`[data-teaser="${key}"]`)) return;
    for (const layer of stage.querySelectorAll('[data-teaser]')) {
      const active = layer.getAttribute('data-teaser') === key;
      layer.classList.toggle('is-active', active);
      layer.setAttribute('aria-hidden', String(!active));
    }
  }

  #resetTeaser() {
    this.#showTeaser('default');
  }

  /* ── Search ─────────────────────────────────────────────────────────────── */

  #onInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches('[data-search-input]')) return;
    this.#pinned = true;
    clearTimeout(this.#searchTimer);
    this.#searchTimer = window.setTimeout(() => this.#search(input.value.trim()), SEARCH_DEBOUNCE);
  };

  #onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form.matches('[data-search-form]')) return;
    const input = form.querySelector('[data-search-input]');
    if (input instanceof HTMLInputElement && !input.value.trim()) event.preventDefault();
  };

  async #search(terms) {
    const results = this.querySelector('[data-search-results]');
    const viewAll = this.querySelector('[data-search-all]');
    if (!results) return;

    // "Alle anzeigen" → the Shop page, which hydrates its own lookup from ?q=.
    if (viewAll instanceof HTMLAnchorElement) {
      const url = new URL(this.dataset.shopUrl || '/collections/all', window.location.origin);
      if (terms) url.searchParams.set('q', terms);
      viewAll.href = url.pathname + url.search;
    }

    this.#searchAbort?.abort();

    if (!terms) {
      this.querySelector('[data-search-form]')?.classList.remove('gg-lookup--loading');
      results.innerHTML = this.#searchDefault;
      return;
    }

    this.#searchAbort = new AbortController();
    const url = new URL(this.dataset.searchUrl || '/search/suggest', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    url.searchParams.set('section_id', 'gg-hd-search-results');

    // The house field's underline doubles as the loading bar (gg-lookup-field-styles).
    const form = this.querySelector('[data-search-form]');
    const signal = this.#searchAbort.signal;
    form?.classList.add('gg-lookup--loading');

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const row = html.querySelector('[data-search-row]');
      // Same products as on screen (a keystroke that didn't change the matches) →
      // keep the row, so the cards' entrance animation doesn't replay for nothing.
      const ids = (root) => [...root.querySelectorAll('[data-product-id]')].map((el) => el.dataset.productId).join();
      if (row && (ids(row) !== ids(results) || !ids(row))) results.replaceChildren(row);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') console.error(error);
    } finally {
      // Only the latest request clears the bar — an aborted one must not.
      if (!signal.aborted) form?.classList.remove('gg-lookup--loading');
    }
  }

  /* ── Cart ───────────────────────────────────────────────────────────────── */

  /**
   * The count is ALWAYS the cart's own total, re-read from /cart.js — never derived
   * from the event. The cart has several writers whose payloads mean different
   * things (product form: quantity ADDED, also on a failed add; cart rows: new
   * TOTAL; quick-add block and promotion gifts: no count at all), so any tally
   * built from them drifts with every add/remove. One small read is always right.
   */
  #onCartUpdate = (event) => {
    this.#syncCartCount();

    const data = event.detail?.data;
    if (data?.source === 'product-form-component' && !data.didError && this.hasAttribute('data-auto-open-cart')) {
      this.open('cart', { pinned: true });
    }
  };

  /** Re-read the cart; only the newest read may write (rapid +/− clicks race). */
  async #syncCartCount() {
    this.#cartAbort?.abort();
    const controller = (this.#cartAbort = new AbortController());
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        signal: controller.signal,
        cache: 'no-store',
      });
      const cart = await response.json();
      if (controller.signal.aborted) return;
      for (const el of this.querySelectorAll('[data-cart-count]')) el.textContent = String(cart.item_count);
    } catch {
      /* aborted by a newer read, or offline — keep what is shown */
    }
  }

  /** Back/forward cache restores a stale count — re-read the cart. */
  #onPageShow = (event) => {
    if (event.persisted) this.#syncCartCount();
  };

  /* ── Phone scroll lock ──────────────────────────────────────────────────── */

  #lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  #unlockScroll() {
    document.documentElement.style.removeProperty('overflow');
  }
}

if (!customElements.get('gg-header')) customElements.define('gg-header', GgHeader);
